'use client';

import { mutate } from 'swr';
import axios, { endpoints } from 'src/utils/axios';
import tairraAxiosInstance from 'src/utils/axios-tairra';
import { apiEndpoints } from '@utils/api-endpoints';
import { setSession, setTairraApiSession } from './utils';
import {
  STORAGE_KEY,
  TAIRRA_STORAGE_KEY,
  EMPLOYEE_ID_KEY,
  IS_ADMIN_KEY,
  IS_EMAIL_VERIFIED_KEY,
  ROLE_KEY,
  EXPIRATION_KEY,
} from './constants';

import { toast } from 'src/components/snackbar';

/** **************************************
 * Sign in
 *************************************** */

export const signInWithPassword = async ({ email, password }) => {
  try {
    const params = { email, password };

    const res = await axios.post(endpoints.auth.signIn, params);

    const { accessToken } = res.data;

    if (!accessToken) {
      throw new Error('Access token not found in response');
    }

    setSession(accessToken);
  } catch (error) {
    console.error('Error during sign in:', error);
    throw error;
  }
};

/** **************************************
 * Tairra Sign in
 *************************************** */

export const signInUserWithPassword = async ({ email, password }) => {
  try {
    const tairraParams = {
      grantType: 'password',
      clientId: 'web',
      userName: email,
      password,
      captchaId: '',
      captchaGuess: '',
      refreshToken: '',
      deviceToken: '',
      device: 2,
    };

    delete tairraAxiosInstance.defaults.headers.common['Authorization'];
    const tairraResponse = await tairraAxiosInstance.post(apiEndpoints.auth.signIn, tairraParams);
    const { access_token: tairraAccessToken } = tairraResponse.data?.data || {};

    // if (!tairraAccessToken) {
    //   throw new Error('Access token not found in response');
    // }

    setTairraApiSession(tairraAccessToken); // Assuming this sets the session with the token
    mutate(() => true, null, { revalidate: false });
    return tairraResponse.data;
  } catch (error) {
    const errorMessage =
      error?.returnMessage?.[0] || error?.message || 'Something went wrong during sign in';

    // Check for the specific error message
    if (errorMessage) {
      console.error('Error during sign in: Incorrect username or password');
      // Optionally, you can throw a specific error to be handled by the UI
      toast.error(errorMessage, { position: 'top-center' });
    } else {
      console.error('Error during sign in:', errorMessage);
      throw new Error(errorMessage);
    }
  }
};

export const exchangeCodeForToken = async (authorizationCode) => {
  try {
    const tokenUrl = 'https://stg-id.uaepass.ae/idshub/token';

    const payload = new URLSearchParams({
      grant_type: 'authorization_code',
      code: authorizationCode,
      redirect_uri: 'https://web.mahami.ae/auth/uaepass',
      client_id: 'dghr_web_stage',
      client_secret: 'QB2SbcIDVdxAksV2',
    });

    const response = await fetch(tokenUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: payload.toString(),
    });

    const data = await response.json();

    if (!data.access_token) {
      throw new Error('Failed to retrieve access token');
    }

    return data;
  } catch (error) {
    console.error('Error exchanging code for token:', error);
    throw error;
  }
};


export const signInUserWithUAEPass = async (UAEPassData) => {
  try {
    delete tairraAxiosInstance.defaults.headers.common['Authorization'];

    const tairraResponse = await tairraAxiosInstance.post(
      apiEndpoints.auth.signInUaePass,
      UAEPassData
    );
    console.log('this is the response', tairraResponse);

    const { access_token: tairraAccessToken } = tairraResponse.data?.data || {};

    if (!tairraAccessToken) {
      throw new Error('Access token not found in response');
    }

    setTairraApiSession(tairraAccessToken);

    mutate(() => true, null, { revalidate: false });

    return tairraResponse.data;
  } catch (error) {



      setTimeout(() => {
        const uaepassLogoutUrl = `https://ids.uaepass.ae/idshub/logout?redirect_uri=https://web.mahami.ae`;
        window.location.href = uaepassLogoutUrl;
      }, 2000);
   return error;
  }
};

/** **************************************
 * Sign up
 *************************************** */
export const signUp = async ({ email, password, firstName, lastName }) => {
  const params = {
    email,
    password,
    firstName,
    lastName,
  };

  try {
    const res = await axios.post(endpoints.auth.signUp, params);

    const { accessToken } = res.data;

    if (!accessToken) {
      throw new Error('Access token not found in response');
    }

    localStorage.setItem(STORAGE_KEY, accessToken);
  } catch (error) {
    console.error('Error during sign up:', error);
    throw error;
  }
};

/** **************************************
 * Sign out
 *************************************** */
export const signOut = async () => {
  try {
    await setTairraApiSession(null);
  } catch (error) {
    console.error('Error during sign out:', error);
    throw error;
  }
};

export async function logoutUser(data) {
  try {
    const response = await tairraAxiosInstance.post(apiEndpoints.auth.logOut, data);

    if (response?.data?.returnStatus) {
      const loginMethod = localStorage.getItem("loginMethod");

      if (loginMethod === "uaepass") {
        setTimeout(() => {
          const uaepassLogoutUrl =
            "https://ids.uaepass.ae/idshub/logout?redirect_uri=https://web.mahami.ae";
          window.location.href = uaepassLogoutUrl;
        }, 100);
      }

      return { success: true, response };
    } else {
      return { success: false };
    }
  } catch (error) {
    const errorMessage = error?.returnMessage?.[0] || "Something went wrong!";
    toast.error(errorMessage, { position: "top-center" });
    console.error("Error in logout:", error);
    return { success: false };
  }
}


export async function forgetPassword(data) {
  try {
    const requestData = { emailAddress: data.email };
    // log("requestData:", requestData);

    const response = await tairraAxiosInstance.post(apiEndpoints.auth.forgetpw, requestData);

    console.log('This is the response', response);
    if (response?.data?.returnStatus) {
      toast.success(response?.data?.returnMessage[0], { position: 'top-center' });
      return { success: true, response };
    } else {
      toast.error(response?.data?.returnMessage[0], { position: 'top-center' });
      return { success: false };
    }
  } catch (error) {
    const errorMessage = error?.returnMessage[0] || ['Something went wrong!'];
    toast.error(errorMessage, { position: 'top-center' });
    console.error('Error in forgetPassword:', error);
    return { success: false };
  }
}

export async function resetMyPassword(data) {
  try {
    const response = await tairraAxiosInstance.post(apiEndpoints.auth.resetPassword, data);

    if (response?.data?.returnStatus) {
      toast.success(response?.data?.returnMessage[0], { position: 'top-center' });
      return { success: true, response };
    } else {
      toast.error(response?.data?.returnMessage[0], { position: 'top-center' });
      return { success: false };
    }
  } catch (error) {
    const errorMessage = error?.returnMessage[0] || ['Something went wrong!'];
    toast.error(errorMessage, { position: 'top-center' });
    console.error('Error in forgetPassword:', error);
    return { success: false };
  }
}

export async function verifyOTP(otp, userId) {
  try {
    const requestData = { otp, userId };
    // log("requestData:", requestData);

    const response = await tairraAxiosInstance.post(apiEndpoints.auth.verifyEmail, requestData);

    console.log('This is the response', response);
    if (response?.data?.returnStatus) {
      toast.success(response?.data?.returnMessage[0]);
      return { success: true, response };
    } else {
      return { success: false };
    }
  } catch (error) {
    const errorMessage = error?.returnMessage || ['Something went wrong!'];
    toast.error(errorMessage[0]);
    console.error('Error :', error);
    return { success: false };
  }
}

export async function resendOTP(userId) {
  try {
    const requestData = { userId };

    const response = await tairraAxiosInstance.post(apiEndpoints.auth.resendOTP, requestData);

    console.log('This is the response', response);
    if (response?.data?.returnStatus) {
      // toast.success(response?.data?.returnMessage[0]);
      return { success: true, response };
    } else {
      return { success: false };
    }
  } catch (error) {
    const errorMessage = error?.returnMessage || ['Something went wrong!'];
    toast.error(errorMessage[0]);
    console.error('Error :', error);
    return { success: false };
  }
}
